<template>
  <form class="reply-form form" novalidate @submit.prevent="save">
    <UsernameInput :model="model" :validation="validation" />
    <InputWithContextMenu
      ref="input"
      v-model="model.text"
      elementType="textarea"
      rows="1"
      type="text"
      class="form-control reply-form__text"
      placeholder="Write a reply..."
      :class="validation.stateClass('text')"
    />
    <FormError :errors="validation.errors" field="text" />
    <button
      v-wait:disabled="'Posting Reply'"
      type="submit"
      class="btns btn-primary"
    >
      <v-wait for="Posting Reply">
        <template v-slot:waiting>
          Posting
          <button-spinner />
        </template>
        Post Reply
      </v-wait>
    </button>
  </form>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import UsernameInput from './UsernameInput.vue'

import { useWait } from '@/app/composables/useWait'
import { ValidationState, setFocus } from '@/helpers/form'
import { backend } from '@/services/backend'
import { Comment, Thread } from '@/models/interfaces'
import { DocView } from '@/models/doc'

const EMPTY_COMMENT = { id: null, text: '' }

const props = defineProps<{
  doc: DocView
  thread: Thread
  comment: Comment
}>()

const wait = useWait()
const emit = defineEmits<{ (e: 'replyPosted', comment: Comment): void }>()

const input = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const validation = ref(new ValidationState())
const model = ref<any>(Object.assign({}, EMPTY_COMMENT))

async function save(): Promise<void> {
  try {
    wait.start('Posting Reply')
    // Send the reply to backend.
    const reply = await backend.saveReply(
      props.doc,
      props.thread,
      props.comment,
      model.value,
    )

    // Update comment replies with new reply.
    /* eslint-disable vue/no-mutating-props */
    props.comment.replies.push(reply)
    // Set the "notify_new_comment" toggle for the parent thread
    if (reply.notify_new_comment !== undefined) {
      props.thread.notify_new_comment = reply.notify_new_comment
    }
    /* eslint-enable vue/no-mutating-props */
    reply.parent = props.comment

    // Reset the form state.
    validation.value.reset()
    model.value = Object.assign({}, EMPTY_COMMENT)

    // Notify parent compoment that the reply was posted
    // (parent component hides the reply form in the even handler).
    emit('replyPosted', props.comment)
  } catch (error) {
    validation.value.showErrors(error)
  } finally {
    wait.end('Posting Reply')
  }
}

function focusInput(): void {
  setFocus(input.value?.$el)
}

onMounted(() => {
  focusInput()
})
</script>
